/**
 * API Client
 * Handles all communication with the Express backend
 *
 * Security: Uses httpOnly cookies for session authentication
 * - Session cookie is set by server on login
 * - Cookie is automatically sent with every request via credentials: 'include'
 * - API key is never stored in browser (only sent once during login)
 */

import type { AnalyticsData, Message } from '@resova/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// CSRF token configuration
const CSRF_COOKIE_NAME = 'csrf-token';
const CSRF_HEADER_NAME = 'x-csrf-token';

/**
 * Get CSRF token from cookie for mutation requests
 * The token is set by the server on login and /me responses
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return value;
    }
  }
  return null;
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  dedupe?: boolean; // Enable request deduplication (default: true for GET, false for mutations)
  signal?: AbortSignal; // Allow external abort control
  timeout?: number; // Request timeout in milliseconds (default: 60000 for chat, 30000 for others)
}

// Default timeouts
const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds for most requests
const CHAT_TIMEOUT_MS = 60000; // 60 seconds for chat (AI responses take longer)

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

// ============================================
// Request Deduplication
// ============================================

/**
 * In-flight request cache for deduplication.
 * Prevents multiple concurrent requests for the same resource.
 *
 * Example: If dashboard mounts two components that both call fetchAnalytics(),
 * only one network request is made and both components get the same result.
 */
interface InFlightRequest<T> {
  promise: Promise<ApiResponse<T>>;
  abortController: AbortController;
}

const inFlightRequests = new Map<string, InFlightRequest<unknown>>();

/**
 * Generate a cache key for deduplication.
 * Combines method, endpoint, and stringified body.
 */
function getDedupeKey(endpoint: string, options: ApiOptions): string {
  const { method = 'GET', body } = options;
  const bodyKey = body ? JSON.stringify(body) : '';
  return `${method}:${endpoint}:${bodyKey}`;
}

/**
 * Cancel an in-flight request by its dedupe key.
 * Useful when navigating away from a page.
 */
export function cancelRequest(endpoint: string, options: ApiOptions = {}): void {
  const key = getDedupeKey(endpoint, options);
  const inFlight = inFlightRequests.get(key);
  if (inFlight) {
    inFlight.abortController.abort();
    inFlightRequests.delete(key);
  }
}

/**
 * Cancel all in-flight requests.
 * Useful during logout or navigation.
 */
export function cancelAllRequests(): void {
  for (const [key, request] of inFlightRequests) {
    request.abortController.abort();
    inFlightRequests.delete(key);
  }
}

/**
 * Base fetch wrapper with cookie-based authentication
 * Automatically includes credentials (cookies) with every request
 *
 * Features:
 * - Request deduplication (prevents concurrent duplicate requests)
 * - AbortController support (cancel in-flight requests)
 * - Automatic retry on abort for non-duplicate requests
 */
async function apiFetch<T>(endpoint: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
  const { method = 'GET', body, headers = {}, dedupe, signal, timeout } = options;

  // Determine if we should dedupe (default: true for GET, false for mutations)
  const shouldDedupe = dedupe ?? (method === 'GET' || method === 'POST');

  // Check for in-flight request with same key
  const dedupeKey = getDedupeKey(endpoint, options);
  if (shouldDedupe) {
    const inFlight = inFlightRequests.get(dedupeKey);
    if (inFlight) {
      // Return existing promise - no duplicate network request
      return inFlight.promise as Promise<ApiResponse<T>>;
    }
  }

  // Create abort controller for this request
  const abortController = new AbortController();

  // If external signal provided, chain it to our abort controller
  if (signal) {
    signal.addEventListener('abort', () => abortController.abort());
  }

  // Set up request timeout
  // Chat endpoints get longer timeout since AI responses take time
  const isChat = endpoint.includes('/chat');
  const timeoutMs = timeout ?? (isChat ? CHAT_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeoutMs);

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add CSRF token for mutation requests (POST, PUT, PATCH, DELETE)
  if (method !== 'GET') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      requestHeaders[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  // Create the fetch promise
  const fetchPromise = (async (): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',  // Include cookies for session auth
        signal: abortController.signal,
      });

      // Handle non-JSON responses (e.g., Vercel 504 gateway timeout)
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType?.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          // JSON parse failed - treat as text error
          const text = await response.text().catch(() => 'Unknown error');
          return {
            success: false,
            error: `Server error: ${text.substring(0, 100)}`,
            statusCode: response.status,
          };
        }
      } else {
        // Non-JSON response (gateway errors, etc.)
        const text = await response.text().catch(() => 'Unknown error');
        return {
          success: false,
          error: response.status === 504
            ? 'Request timed out. Please try a simpler question.'
            : `Server error (${response.status}): ${text.substring(0, 100)}`,
          statusCode: response.status,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}`,
          statusCode: response.status,
        };
      }

      return data;
    } catch (error) {
      // Handle abort gracefully - distinguish timeout from manual cancel
      if (error instanceof Error && error.name === 'AbortError') {
        // Check if this was a timeout (timeout already elapsed)
        const wasTimeout = !signal?.aborted;
        return {
          success: false,
          error: wasTimeout
            ? `Request timed out after ${timeoutMs / 1000}s. Please try again.`
            : 'Request cancelled',
        };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    } finally {
      // Clean up timeout to prevent memory leaks
      clearTimeout(timeoutId);

      // Clean up from in-flight map
      if (shouldDedupe) {
        inFlightRequests.delete(dedupeKey);
      }
    }
  })();

  // Store in in-flight map if deduping
  if (shouldDedupe) {
    inFlightRequests.set(dedupeKey, {
      promise: fetchPromise as Promise<ApiResponse<unknown>>,
      abortController,
    });
  }

  return fetchPromise;
}

// ============================================
// Analytics API
// ============================================

export interface FetchAnalyticsParams {
  dateRange?: string;
  clientDate?: string;  // Client's local date in YYYY-MM-DD format for timezone-correct calculations
}

export interface SyncInfo {
  syncStatus: string;
  lastSyncAt: string | null;
  isSyncing: boolean;
  hasSynced: boolean;
  historicalSyncStatus?: string;
  historicalSyncProgress?: number;
  dataReady?: boolean;
  daysWithData?: number;
  expectedDays?: number;
  earliestDataDate?: string | null;
  syncCounts?: {
    transactions: number;
    dailyAggregates: number;
    monthlyAggregates: number;
  };
}

export interface AnalyticsResponse {
  success: boolean;
  data?: AnalyticsData;
  sync?: SyncInfo;
  error?: string;
}

/**
 * Get client's local date in YYYY-MM-DD format
 * Used to ensure server calculates date ranges in the client's timezone
 */
function getClientLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Fetch analytics data from database aggregates (fast)
 * Uses pre-computed aggregates instead of live API calls
 * Returns sync status so dashboard can show appropriate UI
 */
export async function fetchAnalytics(params: FetchAnalyticsParams = {}): Promise<AnalyticsResponse> {
  // Always include client's local date for timezone-correct calculations
  const requestBody = {
    ...params,
    clientDate: params.clientDate || getClientLocalDate(),
  };

  const response = await apiFetch<{ data: AnalyticsData; sync: SyncInfo }>('/api/analytics/db', {
    method: 'POST',
    body: requestBody,
  });

  if (!response.success) {
    return {
      success: false,
      error: response.error,
    };
  }

  // Extract data and sync info from nested response
  const result = response as unknown as { success: boolean; data: AnalyticsData; sync?: SyncInfo };

  return {
    success: true,
    data: result.data,
    sync: result.sync,
  };
}

/**
 * Fetch analytics data from live Resova APIs (slow, comprehensive)
 * Use only when fresh data is explicitly needed
 */
export async function fetchLiveAnalytics(params: FetchAnalyticsParams = {}): Promise<ApiResponse<AnalyticsData>> {
  return apiFetch<AnalyticsData>('/api/analytics', {
    method: 'POST',
    body: params,
  });
}

// ============================================
// Chat API
// ============================================

export interface ChatParams {
  message: string;
  analyticsData?: AnalyticsData;
  conversationHistory?: Message[];
  sessionId?: string;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  sessionId?: string;
  charts?: Array<{
    type: string;
    title: string;
    dataSource?: string;
    description?: string;
    data?: unknown[];
  }>;
  tables?: Array<{
    title: string;
    headers: string[];
    rows: (string | number | null)[][];
  }>;
  suggestedQuestions?: string[];
}

// Maximum number of message pairs (user + assistant) to keep in history
// Reduced from 5 to 3 to prevent Vercel 60s timeout
const MAX_HISTORY_PAIRS = 3;
// Maximum characters per message content
// Reduced from 4000 to 2000 to keep context smaller
const MAX_MESSAGE_LENGTH = 2000;

/**
 * Trim conversation history to prevent context overflow
 * Keeps the most recent messages and truncates long content
 */
function trimConversationHistory(history: Message[] | undefined): Array<{ role: string; content: string }> {
  if (!history || history.length === 0) return [];

  // Strip charts/tables and only keep role + content
  const lightweight = history.map(msg => ({
    role: msg.role,
    content: msg.content,
  }));

  // Keep only the most recent N message pairs (2 messages per pair = user + assistant)
  const maxMessages = MAX_HISTORY_PAIRS * 2;
  const trimmed = lightweight.length > maxMessages
    ? lightweight.slice(-maxMessages)
    : lightweight;

  // Truncate long messages to prevent token overflow
  return trimmed.map(msg => ({
    role: msg.role,
    content: msg.content.length > MAX_MESSAGE_LENGTH
      ? msg.content.slice(0, MAX_MESSAGE_LENGTH) + '... [truncated]'
      : msg.content,
  }));
}

/**
 * Detect if a message is a complex "Full Analysis" query that needs extended timeout
 * These queries require multiple tool calls and can take 60+ seconds
 */
function isComplexQuery(message: string): boolean {
  const lowerMessage = message.toLowerCase();

  // Detect Full Analysis queries (multilingual patterns)
  const fullAnalysisPatterns = [
    'comprehensive analysis',
    'full analysis',
    'complete analysis',
    'analyse complète',
    'análisis completo',
    'umfassende analyse',
    'all insights',
    'tous les insights',
    'todos los insights',
    'provide a comprehensive',
    'analyze my revenue performance',
    'analyze my operations',
    'analyze my guest experience',
    'analiza mi',
    'analyse ma',
    'analysiere mein',
  ];

  return fullAnalysisPatterns.some(pattern => lowerMessage.includes(pattern));
}

/**
 * Send chat message using database-backed context (fast)
 * Uses pre-computed aggregates for context instead of live API calls
 * Routes complex queries to the worker endpoint with extended timeout
 */
export async function sendChatMessage(params: ChatParams): Promise<ChatResponse> {
  // Trim conversation history to prevent context overflow
  const lightweightHistory = trimConversationHistory(params.conversationHistory);

  // Detect complex queries and route to worker endpoint with 300s timeout
  const useWorker = isComplexQuery(params.message);
  const endpoint = useWorker ? '/api/chat/worker' : '/api/chat/db';

  // Use database-backed chat endpoint - only needs message and history
  // Include clientDate for timezone-correct date calculations in tool calls
  // Complex queries get 3 minute client timeout (worker has 5 min server timeout)
  const response = await apiFetch<ChatResponse>(endpoint, {
    method: 'POST',
    body: {
      message: params.message,
      conversationHistory: lightweightHistory,
      clientDate: getClientLocalDate(),
      sessionId: params.sessionId,
    },
    timeout: useWorker ? 180000 : 60000,  // 3 minutes for complex queries
  });

  // Handle response format from /api/chat/db
  const chatResponse = response as unknown as ChatResponse & { response?: string; error?: string };

  if (!chatResponse.success) {
    const errorMsg = chatResponse.error || response.error || 'Failed to send message';
    return {
      success: false,
      message: errorMsg,
    };
  }

  // /api/chat/db returns 'response' field, normalize to 'message'
  return {
    ...chatResponse,
    message: chatResponse.response || chatResponse.message,
    tables: chatResponse.tables,
    sessionId: chatResponse.sessionId,
  };
}

/**
 * Warm up Claude's prompt cache for faster first response.
 * Fire-and-forget: returns immediately, doesn't throw on error.
 * Call this on dashboard load to pre-populate the 5-min cache.
 */
export async function warmupChat(): Promise<void> {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    // Fire-and-forget - don't await the response
    fetch(`${apiUrl}/api/chat/warmup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    }).catch(() => {
      // Silently ignore errors - warmup is non-critical
    });
  } catch {
    // Silently ignore - warmup is non-critical
  }
}

/**
 * Pre-fetch favorite questions and pillar questions in background.
 * Responses are cached server-side for instant delivery when clicked.
 * Fire-and-forget: returns immediately while prefetch happens in background.
 *
 * Only works if account has prefetch enabled (admin-controlled).
 */
export async function prefetchChat(clientDate?: string, dateRange?: { start: string; end: string }): Promise<void> {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    // Fire-and-forget - don't await the response
    fetch(`${apiUrl}/api/chat/prefetch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        clientDate: clientDate || new Date().toISOString(),
        dateRange,
      }),
    }).catch(() => {
      // Silently ignore errors - prefetch is non-critical
    });
  } catch {
    // Silently ignore - prefetch is non-critical
  }
}

/**
 * Streaming chat callbacks
 */
export interface StreamingChatCallbacks {
  onStatus?: (status: string) => void;
  onText?: (text: string) => void;
  onError?: (error: string) => void;
}

/**
 * Send chat message with streaming response
 * Provides immediate feedback with status updates and progressive text rendering
 */
export async function sendChatMessageStreaming(
  params: ChatParams,
  callbacks: StreamingChatCallbacks
): Promise<ChatResponse> {
  const lightweightHistory = trimConversationHistory(params.conversationHistory);

  // Get auth cookie for the request
  const apiUrl = import.meta.env.VITE_API_URL || '';

  // Build headers with CSRF token
  const streamHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    streamHeaders[CSRF_HEADER_NAME] = csrfToken;
  }

  // AbortController with timeout to prevent hanging SSE connections
  const abortController = new AbortController();
  const STREAM_TIMEOUT_MS = 180000; // 3 minutes for streaming responses
  const timeoutId = setTimeout(() => abortController.abort(), STREAM_TIMEOUT_MS);

  const response = await fetch(`${apiUrl}/api/chat/db/stream`, {
    method: 'POST',
    headers: streamHeaders,
    credentials: 'include',
    signal: abortController.signal,
    body: JSON.stringify({
      message: params.message,
      conversationHistory: lightweightHistory,
      clientDate: getClientLocalDate(),
      sessionId: params.sessionId,
    }),
  });

  if (!response.ok) {
    clearTimeout(timeoutId);
    const errorText = await response.text();
    callbacks.onError?.(errorText || 'Request failed');
    return {
      success: false,
      message: errorText || 'Request failed',
    };
  }

  // Parse SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    clearTimeout(timeoutId);
    return { success: false, message: 'No response body' };
  }

  const decoder = new TextDecoder();
  let fullText = '';
  let finalData: ChatResponse = { success: true, message: '' };

  try {
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      let currentEvent = 'message';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7);
          continue;
        }
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            switch (currentEvent) {
              case 'status':
                callbacks.onStatus?.(data.message);
                break;
              case 'text':
                fullText += data.content;
                callbacks.onText?.(data.content);
                break;
              case 'error':
                callbacks.onError?.(data.message);
                return { success: false, message: data.message };
              case 'done':
                finalData = {
                  success: true,
                  message: data.message || fullText, // Use complete message from done event
                  charts: data.charts,
                  tables: data.tables,
                  suggestedQuestions: data.suggestedQuestions,
                  sessionId: data.sessionId,
                };
                break;
            }
          } catch {
            // Ignore JSON parse errors for incomplete data
          }
          currentEvent = 'message'; // Reset for next event
        }
      }
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      callbacks.onError?.('Streaming response timed out. Please try again.');
      return { success: false, message: 'Streaming response timed out. Please try again.' };
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    reader.releaseLock();
  }

  return finalData;
}

/**
 * Send chat message using live API data (slow, comprehensive)
 * Use only when fresh data is explicitly needed
 */
export async function sendLiveChatMessage(params: ChatParams): Promise<ChatResponse> {
  // Trim conversation history to prevent context overflow
  const lightweightHistory = trimConversationHistory(params.conversationHistory);

  const response = await apiFetch<ChatResponse>('/api/chat', {
    method: 'POST',
    body: {
      message: params.message,
      analyticsData: params.analyticsData,
      conversationHistory: lightweightHistory,
    },
  });

  const chatResponse = response as unknown as ChatResponse;

  if (!chatResponse.success) {
    return {
      success: false,
      message: chatResponse.message || response.error || 'Failed to send message',
    };
  }

  return chatResponse;
}

// ============================================
// Chat Sessions API
// ============================================

export interface ChatSession {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

/**
 * Get all chat sessions for the current user
 */
export async function getChatSessions(): Promise<ApiResponse<ChatSession[]>> {
  const response = await apiFetch<{ sessions: ChatSession[] }>('/api/chat/sessions');
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; sessions: ChatSession[] };
  return { success: true, data: data.sessions };
}

/**
 * Get a specific chat session with all messages
 */
export async function getChatSession(sessionId: string): Promise<ApiResponse<ChatSession>> {
  const response = await apiFetch<{ session: ChatSession }>(`/api/chat/sessions/${sessionId}`);
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; session: ChatSession };
  return { success: true, data: data.session };
}

/**
 * Delete a chat session
 */
export async function deleteChatSession(sessionId: string): Promise<ApiResponse<void>> {
  return apiFetch<void>(`/api/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

// ============================================
// Language API
// ============================================

/**
 * Update user's language preference
 */
export async function updateLanguage(language: string): Promise<ApiResponse<{ language: string }>> {
  return apiFetch<{ language: string }>('/api/auth/language', {
    method: 'PATCH',
    body: { language },
  });
}

// ============================================
// Favorites API
// ============================================

export interface FavoritePrompt {
  id: string;
  text: string;
  source: 'custom' | 'pillar';
  pillarId?: string;
  icon?: string;
  createdAt: number;
}

export type AddFavoriteParams = Omit<FavoritePrompt, 'id' | 'createdAt'>;

/**
 * Get user's favorite prompts
 */
export async function getFavorites(): Promise<ApiResponse<FavoritePrompt[]>> {
  const response = await apiFetch<{ favorites: FavoritePrompt[] }>('/api/auth/favorites');
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; favorites: FavoritePrompt[] };
  return { success: true, data: data.favorites };
}

/**
 * Add a new favorite prompt
 * Returns both the new favorite and the full updated favorites array
 */
export async function addFavorite(params: AddFavoriteParams): Promise<ApiResponse<{ favorite: FavoritePrompt; favorites: FavoritePrompt[] }>> {
  const response = await apiFetch<{ favorite: FavoritePrompt; favorites: FavoritePrompt[] }>('/api/auth/favorites', {
    method: 'POST',
    body: params,
  });
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; favorite: FavoritePrompt; favorites: FavoritePrompt[] };
  return { success: true, data: { favorite: data.favorite, favorites: data.favorites } };
}

/**
 * Delete a favorite prompt
 * Returns the updated favorites array
 */
export async function deleteFavorite(id: string): Promise<ApiResponse<FavoritePrompt[]>> {
  const response = await apiFetch<{ favorites: FavoritePrompt[] }>(`/api/auth/favorites/${id}`, {
    method: 'DELETE',
  });
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; favorites: FavoritePrompt[] };
  return { success: true, data: data.favorites };
}

/**
 * Update entire favorites list (for reordering)
 */
export async function updateFavorites(favorites: FavoritePrompt[]): Promise<ApiResponse<FavoritePrompt[]>> {
  const response = await apiFetch<{ favorites: FavoritePrompt[] }>('/api/auth/favorites', {
    method: 'PUT',
    body: { favorites },
  });
  if (!response.success) {
    return { success: false, error: response.error };
  }
  const data = response as unknown as { success: boolean; favorites: FavoritePrompt[] };
  return { success: true, data: data.favorites };
}

// ============================================
// Health API
// ============================================

export interface HealthResponse {
  status: 'ok' | 'degraded';
  timestamp: string;
  version: string;
  services: {
    database: 'connected' | 'disconnected';
  };
}

export async function checkHealth(): Promise<ApiResponse<HealthResponse>> {
  return apiFetch<HealthResponse>('/api/health');
}

// ============================================
// Sync API
// ============================================

export interface SyncResult {
  success: boolean;
  accountId?: string;
  syncType?: string;
  recordsFetched?: number;
  recordsInserted?: number;
  recordsUpdated?: number;
  aggregatesUpdated?: number;
  durationMs?: number;
  progress?: number; // 0-100, for tracking sync completion
  complete?: boolean; // true when progress === 100
  error?: string;
}

export interface SyncStatus {
  success: boolean;
  synced: boolean;
  message?: string;
  account?: {
    id: string;
    name: string;
    syncStatus: string;
    historicalSyncStatus?: string; // 'pending' | 'syncing' | 'completed'
    historicalSyncProgress?: number; // 0-100
    lastSyncAt: string | null;
    lastSyncError: string | null;
    createdAt: string;
  };
  counts?: {
    transactions: number;
    dailyAggregates: number;
    monthlyAggregates: number;
  };
  latestSync?: {
    syncType: string;
    status: string;
    recordsFetched: number;
    recordsInserted: number;
    recordsUpdated: number;
    aggregatesUpdated: number;
    startedAt: string;
    completedAt: string | null;
  };
}

/**
 * Trigger initial sync for an account
 * Uses the 300s worker endpoint for comprehensive sync (480 days back + 120 days forward).
 * Returns progress for polling - call triggerBackfill repeatedly until progress === 100.
 *
 * Authentication:
 * - If logged in: Uses session cookie automatically (API key not needed)
 * - During onboarding: Requires resovaApiKey parameter (before session exists)
 */
export async function triggerInitialSync(accountName?: string, resovaApiKey?: string): Promise<ApiResponse<SyncResult>> {
  const headers: Record<string, string> = {};
  if (resovaApiKey) {
    headers['x-resova-api-key'] = resovaApiKey;
  }

  return apiFetch<SyncResult>('/api/sync/worker/initial', {
    method: 'POST',
    body: { accountName },
    headers,
  });
}

/**
 * Continue sync from where initial sync left off
 * Call repeatedly until progress === 100 (sync complete).
 *
 * Authentication:
 * - If logged in: Uses session cookie automatically (API key not needed)
 * - During onboarding: Requires resovaApiKey parameter (before session exists)
 */
export async function triggerBackfill(resovaApiKey?: string): Promise<ApiResponse<SyncResult>> {
  const headers: Record<string, string> = {};
  if (resovaApiKey) {
    headers['x-resova-api-key'] = resovaApiKey;
  }

  return apiFetch<SyncResult>('/api/sync/worker/backfill', {
    method: 'POST',
    headers,
  });
}

/**
 * Trigger incremental sync for an account
 * Fetches only new data since last sync
 *
 * Authentication: Uses session cookie automatically (must be logged in)
 * The server retrieves the API key from the account record
 */
export async function triggerIncrementalSync(): Promise<ApiResponse<SyncResult>> {
  return apiFetch<SyncResult>('/api/sync/incremental', {
    method: 'POST',
  });
}

/**
 * Get sync status for an account
 *
 * Authentication:
 * - If logged in: Uses session cookie automatically (API key not needed)
 * - During onboarding: Requires resovaApiKey parameter (before session exists)
 */
export async function getSyncStatus(resovaApiKey?: string): Promise<ApiResponse<SyncStatus>> {
  const headers: Record<string, string> = {};
  if (resovaApiKey) {
    headers['x-resova-api-key'] = resovaApiKey;
  }
  return apiFetch<SyncStatus>('/api/sync/status', { headers });
}

// ============================================
// Auth API
// ============================================

export interface UserInfo {
  id: string;
  name: string | null;
  email: string;
  role: string;
  language: string;
}

export interface AccountInfo {
  id: string;
  name: string;
  syncStatus: string;
  lastSyncAt: string | null;
  timezone?: string;
  currency?: string;
  activityTypes?: string[];
  onboardingCompletedAt?: string | null;
  giftPaymentsActive?: boolean | null; // V1=false (gift as promotion), V2=true (gift as payment)
}

export interface LoginResponse {
  success: boolean;
  user?: UserInfo;
  account?: AccountInfo;
  error?: string;
}

export interface LogoutResponse {
  success: boolean;
  message?: string;
}

export interface SessionResponse {
  success: boolean;
  authenticated: boolean;
  user?: UserInfo;
  account?: AccountInfo;
  error?: string;
  impersonating?: {
    viewOnly: boolean;
    accountName?: string;
  } | null;
}

/**
 * Login with email and password
 * Creates a secure session and sets httpOnly cookie
 */
export async function loginWithEmail(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Login with Resova API key (legacy/onboarding support)
 * Creates a secure session and sets httpOnly cookie
 * API key is sent once, then session cookie handles auth
 */
export async function login(resovaApiKey: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-resova-api-key': resovaApiKey,
    },
    credentials: 'include',  // Important: receive and store the session cookie
  });

  const data = await response.json();
  return data;
}

/**
 * Logout and clear session
 * Server will delete session and clear httpOnly cookie
 */
export async function logout(): Promise<LogoutResponse> {
  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers[CSRF_HEADER_NAME] = csrfToken;
  }

  const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

  const data = await response.json();
  return data;
}

/**
 * Get current session info
 * Used to check if user is authenticated on page load
 */
export async function getSession(): Promise<SessionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    method: 'GET',
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Initialize a ResovAI session from a Resova-signed JWT (RP-2566 SSO entry point).
 *
 * The caller is responsible for reading the Resova session cookie value and
 * passing it here. The token is sent as a Bearer credential so the backend can
 * validate it against the shared RESOVA_JWT_SECRET.
 *
 * On success, the server sets the ResovAI httpOnly `session` cookie and
 * returns the same shape as POST /api/auth/login.
 */
export async function initWithResovaToken(token: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/init`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();
  return data;
}

